import { ApplicationConfig, ErrorHandler, provideZonelessChangeDetection } from '@angular/core';
import {
  provideRouter,
  PreloadAllModules,
  withPreloading,
  withInMemoryScrolling,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { provideNgxStripe } from 'ngx-stripe';
import { environment } from '../environments/environment';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

import { routes } from './app.routes';
import { SailsService } from './services/sails.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { EnviosService } from './componentes/paginas/envios/envios.service';
import { PlantillasService } from './componentes/paginas/plantillas/plantillas.service';
import { ActualizadorService } from './services/actualizador.service';
import { TourService } from './services/tour-service/tour.service';
import { ErrorService } from './services/error.service';

const CertifirmPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#e8f4ff',
      100: '#d1e9ff',
      200: '#aad4ff',
      300: '#7dbeff',
      400: '#4f9fff',
      500: '#2ea3f2',
      600: '#1c7fd1',
      700: '#1565aa',
      800: '#0f4a7a',
      900: '#0a344f',
      950: '#051d2d',
    },
    colorScheme: {
      light: {
        surface: {
          ground: '#f9fafc',
        },
      },
      dark: {
        surface: {
          ground: '#0a0a0a',
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
    ),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      theme: {
        preset: CertifirmPreset,
        options: {
          darkModeSelector: '.dark-mode',
        },
      },
      ripple: true,
      inputStyle: 'outlined',
      translation: {
        startsWith: 'Empieza con',
        contains: 'Contiene',
        notContains: 'No contiene',
        endsWith: 'Termina con',
        equals: 'Igual a',
        notEquals: 'Diferente de',
        noFilter: 'Sin filtro',
        lt: 'Menor que',
        lte: 'Menor o igual a',
        gt: 'Mayor que',
        gte: 'Mayor o igual a',
        is: 'Es',
        isNot: 'No es',
        before: 'Antes',
        after: 'Después',
        dateIs: 'La fecha es',
        dateIsNot: 'La fecha no es',
        dateBefore: 'Fecha anterior a',
        dateAfter: 'Fecha posterior a',
        clear: 'Limpiar',
        apply: 'Aplicar',
        matchAll: 'Coincidir todo',
        matchAny: 'Coincidir con cualquiera',
        addRule: 'Añadir regla',
        removeRule: 'Eliminar regla',
        accept: 'Sí',
        reject: 'No',
        choose: 'Elegir',
        upload: 'Subir',
        cancel: 'Cancelar',
        dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
        dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
        dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
        monthNames: [
          'enero',
          'febrero',
          'marzo',
          'abril',
          'mayo',
          'junio',
          'julio',
          'agosto',
          'septiembre',
          'octubre',
          'noviembre',
          'diciembre',
        ],
        monthNamesShort: [
          'ene',
          'feb',
          'mar',
          'abr',
          'may',
          'jun',
          'jul',
          'ago',
          'sep',
          'oct',
          'nov',
          'dic',
        ],
        today: 'Hoy',
        weekHeader: 'Sem',
        firstDayOfWeek: 1,
        dateFormat: 'dd/mm/yy',
        weak: 'Débil',
        medium: 'Medio',
        strong: 'Fuerte',
        passwordPrompt: 'Introduce una contraseña',
        emptyFilterMessage: 'Sin opciones disponibles',
        emptyMessage: 'No se han encontrado resultados',
      },
    }),
    // Services
    SailsService,
    MessageService,
    ConfirmationService,
    EnviosService,
    PlantillasService,
    ActualizadorService,
    TourService,
    {
      provide: ErrorHandler,
      useClass: ErrorService,
    },
    provideNgxStripe(environment.stripe_public_key),
  ],
};
